/** Projects list — port of the original views.projects. `?s=<status>` presets the status filter; `?f=overdue|stale` adds a flag chip. */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { PROJECT_STATUSES, type Backup, type Project } from '@/types/models';
import { fmtDate } from '@/domain/format';
import { isOverdue, isStale } from '@/domain/projects';
import { useAction, useDataset } from '@/hooks/data';
import { PermissionGate } from '@/auth/ProtectedRoute';
import { useAuth } from '@/app/AppContext';
import { ActionsCell, DeadlineCell, ErrorState, IconButton, LoadingState, NoRecords, ProjectLinks, SearchBox, StatusBadge, Topbar, useConfirm } from '@/ui/kit';
import { useAppActions } from '../actions';

type Flag = '' | 'overdue' | 'stale';
const FLAG_LABEL: Record<Exclude<Flag, ''>, string> = { overdue: 'Past deadline', stale: 'No project log in 2+ days' };

export function ProjectsPage() {
  const [q] = useSearchParams();
  const f = q.get('f');
  const flag: Flag = f === 'overdue' || f === 'stale' ? f : '';
  // A new query (e.g. clearing the flag chip) starts the page afresh, as the original re-rendered it.
  return <ProjectsView key={q.toString()} initialStatus={q.get('s') || ''} flag={flag} />;
}

function ProjectsView({ initialStatus, flag }: { initialStatus: string; flag: Flag }) {
  const ds = useDataset();
  const act = useAppActions();
  const header = (
    <Topbar title="Projects" sub="Every project, its service, timeline, status and team">
      <PermissionGate perm="projects:write">
        <button className="btn primary" onClick={() => act.project()}>
          + Add Project
        </button>
      </PermissionGate>
    </Topbar>
  );
  if (ds.error) return (<>{header}<ErrorState error={ds.error} onRetry={ds.refetch} /></>);
  if (!ds.data) return (<>{header}<LoadingState /></>);
  return (
    <>
      {header}
      <ProjectsTable data={ds.data} initialStatus={initialStatus} flag={flag} />
    </>
  );
}

function ProjectsTable({ data, initialStatus, flag }: { data: Backup; initialStatus: string; flag: Flag }) {
  const { projects, updates } = data;
  const nav = useNavigate();
  const act = useAppActions();
  const auth = useAuth();
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(initialStatus);
  const del = useAction(async (s, id: string) => {
    await s.projects.remove(id);
    return true;
  }, 'Project deleted successfully.');
  const list = useMemo(() => {
    const s = search.toLowerCase();
    const flagged = (p: Project) => (flag === 'overdue' ? isOverdue(p) : flag === 'stale' ? isStale(p, updates) : true);
    return projects.filter((p) => flagged(p) && (!status || p.status === status) && (!s || [p.name, p.service, p.lead, p.status].join(' ').toLowerCase().includes(s)));
  }, [projects, updates, search, status, flag]);
  const setDeadline = auth.can('projects:write') ? (id: string) => act.deadline(id) : undefined;
  const remove = (p: Project) =>
    confirm(`Are you sure you want to delete project ${p.name}? Its work logs and daily updates will also be deleted.`, async () => {
      if (await del.run(p.id)) nav('/projects');
    });
  return (
    <div className="panel">
      <div className="panel-h">
        <div className="row grow">
          <SearchBox value={search} onChange={setSearch} placeholder="Search projects" />
          <select style={{ width: 'auto' }} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            <option value="">All statuses</option>
            {PROJECT_STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          {flag ? (
            <span className="chip">
              {FLAG_LABEL[flag]}
              <button type="button" data-tip="Clear" aria-label="Clear" onClick={() => nav('/projects')}>
                ×
              </button>
            </span>
          ) : null}
        </div>
        <span className="hint">
          {list.length} of {projects.length}
        </span>
      </div>
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>SR. No.</th>
              <th>Project</th>
              <th>Service</th>
              <th>Lead</th>
              <th>Status</th>
              <th>Deadline</th>
              <th>Members</th>
              <th>Links</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length ? (
              list.map((p, i) => (
                <tr key={p.id}>
                  <td>{i + 1}</td>
                  <td>
                    <Link to={`/projects/${encodeURIComponent(p.id)}`} style={{ fontWeight: 600, textDecoration: 'none' }}>
                      {p.name}
                    </Link>
                  </td>
                  <td>{p.service || '—'}</td>
                  <td>{p.lead || '—'}</td>
                  <td>
                    <StatusBadge status={p.status} />
                  </td>
                  <td>
                    <DeadlineCell project={p} onSet={setDeadline && (() => setDeadline(p.id))} />
                    {p.revised ? <div className="hint">was {fmtDate(p.planned)}</div> : null}
                  </td>
                  <td>{p.members.length}</td>
                  <td className="link-cell">{p.links.length ? <ProjectLinks project={p} /> : '—'}</td>
                  <td>
                    <ActionsCell>
                      <IconButton icon="view" tip="View" onClick={() => nav(`/projects/${encodeURIComponent(p.id)}`)} />
                      <PermissionGate perm="projects:write">
                        <IconButton icon="edit" tip="Edit" onClick={() => act.project(p.id)} />
                        <IconButton icon="cal" tip="Change deadline" onClick={() => act.deadline(p.id)} />
                        <IconButton icon="del" tip="Delete" danger onClick={() => remove(p)} />
                      </PermissionGate>
                    </ActionsCell>
                  </td>
                </tr>
              ))
            ) : (
              <NoRecords cols={9} />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
